import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { MoneyInput } from "../components/app/audit/MoneyInput";
import { parseMoney } from "../lib/auditMath";
import "./GapsAudit.css";

const REVENUE_RANGES = [
  "Pre-revenue",
  "< ₹10 Lakhs",
  "₹10 Lakhs – ₹50 Lakhs",
  "₹50 Lakhs – ₹1 Crore",
  "₹1 Crore – ₹5 Crore",
  "₹5 Crore – ₹10 Crore",
  "₹10 Crore – ₹50 Crore",
  "₹50 Crore +",
];
const ORG_STRENGTHS = [
  "Solo founder",
  "2 – 10",
  "11 – 50",
  "51 – 200",
  "201 – 500",
  "500 +",
];
const GROWTH_RANGES = [
  "0 – 25 %",
  "25 – 50 %",
  "50 – 100 %",
  "100 – 200 %",
  "200 – 500 %",
  "500 % +",
];

export default function AuthPage({ mode = "login" }) {
  const { login, register, forgotPassword } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isLogin = mode === "login";
  const [step, setStep] = useState(1); // 1 = personal, 2 = business
  const [forgotOpen, setForgotOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    whatsapp: "",
    email: loc.state?.email || "",
    password: "",
    business_name: "",
    industry: "",
    annual_revenue: "",
    annual_profit_inr: "",
    org_strength: "",
    projected_growth: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const prefill = loc.state?.email;
    if (prefill) setForm((f) => ({ ...f, email: prefill }));
  }, [loc.state?.email]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validateStep1 = () => {
    if (!form.name.trim()) return "Please enter your full name";
    if (!form.whatsapp.trim() || form.whatsapp.replace(/\D/g, "").length < 7)
      return "Please enter a valid WhatsApp number";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email))
      return "Please enter a valid email";
    if (!form.password || form.password.length < 6)
      return "Password must be at least 6 characters";
    return null;
  };

  const validateStep2 = () => {
    if (parseMoney(form.annual_profit_inr).error) return parseMoney(form.annual_profit_inr).error;
    if (!form.business_name.trim()) return "Please enter your business name";
    if (!form.industry.trim()) return "Please enter your industry";
    if (!form.annual_revenue) return "Please select your annual revenue range";
    if (!form.org_strength) return "Please select your organisation size";
    if (!form.projected_growth)
      return "Please select your projected growth range";
    return null;
  };

  const goToStep2 = (e) => {
    e.preventDefault();
    const err = validateStep1();
    if (err) {
      toast.error(err);
      return;
    }
    setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isLogin) {
        await login(form.email, form.password);
      } else {
        const err = validateStep1() || validateStep2();
        if (err) {
          toast.error(err);
          setBusy(false);
          return;
        }
        await register({ ...form, annual_profit_inr: parseMoney(form.annual_profit_inr).value });
      }
      const dest = loc.state?.from || "/dashboard";
      nav(dest, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((item) => item.msg).join("; ") : typeof detail === "string" ? detail : "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left visual panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden border-r border-zinc-900">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1704566204689-cf13b20d65b6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBncmVlbiUyMGFuZCUyMGdvbGQlMjB3YXZlcyUyMHRleHR1cmV8ZW58MHx8fHwxNzc3Mzc0MzU4fDA&ixlib=rb-4.1.0&q=85)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(9,9,11,0.65), rgba(9,9,11,0.95))",
          }}
        />
        <div className="relative z-10">
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/bbh_logo.png`}
            alt="Business Breakthrough Hub"
            className="h-20 w-auto object-contain"
          />
        </div>

        <div className="relative z-10">
          <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-400 mb-4">
            // a central hub of ai intelligence
          </div>
          <h1 className="font-display text-5xl xl:text-6xl tracking-tighter font-black leading-[0.95]">
            One Hub.
            <br />
            <span className="text-emerald-400">Many AI agents.</span>
            <br />
            <span className="text-amber-300/90">Endless possibilities.</span>
          </h1>
          <p className="mt-6 text-zinc-400 max-w-md text-[15px] leading-relaxed">
            A powerful AI workspace where every GPT is built for a specific
            business task. Create and execute all from one intelligent hub.
          </p>
        </div>

        <div className="relative z-10 flex items-center gap-8 text-[10px] uppercase tracking-[0.3em] text-zinc-600">
          <div>
            <div className="text-emerald-400 text-base font-display font-bold">
              ∞
            </div>
            agents
          </div>
          <div>
            <div className="text-emerald-400 text-base font-display font-bold">
              24/7
            </div>
            access
          </div>
          <div>
            <div className="text-amber-300 text-base font-display font-bold">
              v1
            </div>
            hub
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 relative">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/bbh_favicon.png`}
              alt="Business Breakthrough Hub"
              className="w-9 h-9 rounded-md object-contain"
            />
            <div className="leading-tight">
              <div className="font-display font-extrabold tracking-tighter text-white">
                BUSINESS <span className="text-amber-400">BREAKTHROUGH</span> HUB
              </div>
              <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500">
                automate your business using ai
              </div>
            </div>
          </div>

          <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
            {isLogin
              ? "// access your hub"
              : step === 1
              ? "// step 1 of 2 · about you"
              : "// step 2 of 2 · about your business"}
          </div>
          <h2 className="font-display text-4xl tracking-tighter font-black mb-2">
            {isLogin
              ? "Welcome back."
              : step === 1
              ? "Create your account."
              : "Tell us about your business."}
          </h2>
          <p className="text-sm text-zinc-500 mb-6">
            {isLogin
              ? "Enter your credentials to step into your hub."
              : step === 1
              ? "All fields are required."
              : "Fields marked * are required. Annual profit is optional."}
          </p>

          {/* Step indicator (signup only) */}
          {!isLogin && (
            <div className="flex items-center gap-3 mb-8">
              <div className="flex items-center gap-2">
                <span
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold border ${
                    step >= 1
                      ? "bg-emerald-600 border-emerald-500 text-white"
                      : "border-zinc-700 text-zinc-500"
                  }`}
                >
                  {step > 1 ? <Check className="w-3.5 h-3.5" /> : "1"}
                </span>
                <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-400">
                  You
                </span>
              </div>
              <div
                className={`flex-1 h-px ${
                  step > 1 ? "bg-emerald-600/60" : "bg-zinc-800"
                }`}
              />
              <div className="flex items-center gap-2">
                <span
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold border ${
                    step >= 2
                      ? "bg-emerald-600 border-emerald-500 text-white"
                      : "border-zinc-700 text-zinc-500"
                  }`}
                >
                  2
                </span>
                <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-400">
                  Business
                </span>
              </div>
            </div>
          )}

          {/* LOGIN form */}
          {isLogin && (
            <form onSubmit={submit} className="space-y-5">
              <FieldText
                label="Email"
                testid="auth-email-input"
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                placeholder="you@studio.com"
              />
              <FieldText
                label="Password"
                testid="auth-password-input"
                type="password"
                required
                value={form.password}
                onChange={update("password")}
                placeholder="••••••••"
              />
              <div className="flex justify-end -mt-2">
                <button
                  type="button"
                  data-testid="auth-forgot-link"
                  onClick={() => setForgotOpen(true)}
                  className="text-xs text-zinc-500 hover:text-emerald-300 transition-colors"
                >
                  Forgot password?
                </button>
              </div>
              <button
                data-testid="auth-submit-btn"
                type="submit"
                disabled={busy}
                className="w-full mt-6 inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl px-6 py-3.5 transition-all glow-primary disabled:opacity-60"
              >
                {busy ? "..." : "Enter the Hub"}
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          )}

          {/* SIGNUP — Step 1 */}
          {!isLogin && step === 1 && (
            <form onSubmit={goToStep2} className="space-y-5">
              <FieldText
                label="Full Name *"
                testid="signup-name-input"
                required
                value={form.name}
                onChange={update("name")}
                placeholder="Ada Lovelace"
              />
              <FieldText
                label="WhatsApp Phone Number *"
                testid="signup-whatsapp-input"
                type="tel"
                required
                value={form.whatsapp}
                onChange={update("whatsapp")}
                placeholder="+91 98765 43210"
              />
              <FieldText
                label="Email ID *"
                testid="auth-email-input"
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                placeholder="you@business.com"
              />
              <FieldText
                label="Password *"
                testid="auth-password-input"
                type="password"
                required
                value={form.password}
                onChange={update("password")}
                placeholder="At least 6 characters"
              />
              <button
                data-testid="signup-step1-next"
                type="submit"
                className="w-full mt-6 inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl px-6 py-3.5 transition-all glow-primary"
              >
                Continue to business details
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          )}

          {/* SIGNUP — Step 2 */}
          {!isLogin && step === 2 && (
            <form onSubmit={submit} className="space-y-5">
              <FieldText
                label="What business are you running? *"
                testid="signup-business-input"
                required
                value={form.business_name}
                onChange={update("business_name")}
                placeholder="e.g., Premium leather goods, B2B consulting"
              />
              <FieldText
                label="Industry *"
                testid="signup-industry-input"
                required
                value={form.industry}
                onChange={update("industry")}
                placeholder="e.g., Fashion, SaaS, Healthcare"
              />
              <FieldSelect
                label="Current Annual Revenue *"
                testid="signup-revenue-input"
                required
                value={form.annual_revenue}
                onChange={update("annual_revenue")}
                options={REVENUE_RANGES}
                placeholder="Select a range"
              />
              <FieldSelect
                label="Organisation Strength *"
                testid="signup-orgstrength-input"
                required
                value={form.org_strength}
                onChange={update("org_strength")}
                options={ORG_STRENGTHS}
                placeholder="Select team size"
              />
              <MoneyInput id="signup-annual-profit-input" label="Annual profit (₹) — optional" showLabel
                value={form.annual_profit_inr} onChange={(value) => setForm((current) => ({ ...current, annual_profit_inr: value }))} />
              <FieldSelect
                label="Expected / Projected Annual Growth *"
                testid="signup-growth-input"
                required
                value={form.projected_growth}
                onChange={update("projected_growth")}
                options={GROWTH_RANGES}
                placeholder="Select a range"
              />

              <div className="flex items-center gap-3 pt-3">
                <button
                  data-testid="signup-step2-back"
                  type="button"
                  onClick={() => setStep(1)}
                  className="inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-xl border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-700 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button
                  data-testid="auth-submit-btn"
                  type="submit"
                  disabled={busy}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl px-6 py-3.5 transition-all glow-primary disabled:opacity-60"
                >
                  {busy ? "Creating..." : "Create Account"}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>
          )}

          <div className="mt-8 text-sm text-zinc-500 text-center">
            {isLogin ? (
              <>
                New here?{" "}
                <Link
                  to="/signup"
                  data-testid="goto-signup"
                  className="text-emerald-400 hover:text-emerald-300 font-semibold"
                >
                  Create an account
                </Link>
              </>
            ) : (
              <>
                Already a member?{" "}
                <Link
                  to="/login"
                  data-testid="goto-login"
                  className="text-emerald-400 hover:text-emerald-300 font-semibold"
                >
                  Sign in
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
      {forgotOpen && (
        <ForgotPasswordModal
          initialEmail={form.email}
          onClose={() => setForgotOpen(false)}
          onSubmit={async (email, message) => {
            await forgotPassword(email, message);
          }}
        />
      )}
    </div>
  );
}

const ForgotPasswordModal = ({ initialEmail, onClose, onSubmit }) => {
  const nav = useNavigate();
  const [email, setEmail] = useState(initialEmail || "");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email");
      return;
    }
    setBusy(true);
    setNotFound(false);
    try {
      await onSubmit(email, message);
      setSent(true);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 404) {
        setNotFound(true);
      } else if (status === 429) {
        toast.error(typeof detail === "string" ? detail : "Too many attempts. Please wait and try again.");
      } else {
        toast.error("Could not file your request. Please try again later.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="forgot-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950 p-7 grain">
        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300/80">
          // forgotten password
        </div>
        <h2 className="font-display text-2xl tracking-tighter font-black mt-1">
          {sent ? "Request received" : "Reset your password"}
        </h2>

        {!sent ? (
          <>
            <p className="text-sm text-zinc-400 mt-2">
              File a request and our admin will set a fresh temporary password
              for you, then send it on WhatsApp. You'll change it on first login.
            </p>
            <form onSubmit={submit} className="mt-5 space-y-4">
              <FieldText
                label="Email"
                testid="forgot-email"
                type="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setNotFound(false);
                }}
                placeholder="you@studio.com"
              />
              {notFound && (
                <div
                  data-testid="forgot-not-found"
                  role="alert"
                  className="rounded-md border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2.5 text-sm text-amber-200"
                >
                  No account exists with <span className="font-semibold">{email}</span>. Please create an account first.
                  <button
                    type="button"
                    data-testid="forgot-go-signup"
                    onClick={() => {
                      onClose();
                      nav("/signup", { state: { email } });
                    }}
                    className="ml-2 underline underline-offset-2 text-emerald-300 hover:text-emerald-200"
                  >
                    Create account
                  </button>
                </div>
              )}
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  Message (optional)
                </label>
                <textarea
                  data-testid="forgot-message"
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Anything the admin should know…"
                  className="w-full bg-transparent border-0 border-b-2 border-zinc-800 focus:border-emerald-500 outline-none py-3 text-base text-white transition-colors placeholder:text-zinc-700 resize-none"
                />
              </div>
              <div className="flex items-center gap-2 justify-end pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={busy}
                  className="text-[11px] uppercase tracking-[0.2em] px-4 py-2 rounded-md border border-zinc-800 text-zinc-400 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="forgot-submit"
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] px-4 py-2 rounded-md border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
                >
                  {busy ? "Sending…" : "Send request"}
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <p className="text-sm text-zinc-300 mt-3">
              Your account <span className="text-emerald-300">{email}</span> was found and our admin has been notified. You'll be contacted on WhatsApp shortly with a temporary password.
            </p>
            <button
              data-testid="forgot-done"
              onClick={onClose}
              className="w-full mt-6 inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg px-4 py-2.5 transition-colors glow-primary"
            >
              Back to sign in
              <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const FieldText = ({ label, testid, type = "text", required, value, onChange, placeholder }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-1.5">
      {label}
    </label>
    <input
      data-testid={testid}
      required={required}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full bg-transparent border-0 border-b-2 border-zinc-800 focus:border-emerald-500 outline-none py-3 text-base text-white transition-colors placeholder:text-zinc-700"
    />
  </div>
);

const FieldSelect = ({ label, testid, required, value, onChange, options, placeholder }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
      {label}
    </label>
    <select
      data-testid={testid}
      required={required}
      value={value}
      onChange={onChange}
      className="w-full bg-transparent border-0 border-b-2 border-zinc-800 focus:border-emerald-500 outline-none py-3 text-base text-white transition-colors appearance-none cursor-pointer"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2371717a' stroke-width='2'><polyline points='6 9 12 15 18 9'/></svg>\")",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 0.25rem center",
      }}
    >
      <option value="" disabled className="bg-zinc-950 text-zinc-500">
        {placeholder || "Select"}
      </option>
      {options.map((o) => (
        <option key={o} value={o} className="bg-zinc-950 text-white">
          {o}
        </option>
      ))}
    </select>
  </div>
);
